import { AxiosError } from 'axios';

export const ConflictErrorStatus = 409;

/** Extracts a human-readable failure reason from an AxiosError, falling back
 *  to the caller's `fallbackMessage` for anything else. Tries, in order:
 *    1. NestJS-style `error.response.data.message` (string or string[])
 *    2. Other common response shapes: `data.error`, plain-string `data`, or
 *       a `details`/`detail` field
 *    3. Axios's own `error.message` (e.g. "Request failed with status code 500"),
 *       unless it's a generic network failure
 *    4. `fallbackMessage`
 *  Non-axios throwables fall straight through to `fallbackMessage` so plain JS
 *  errors like "Cannot read property X of undefined" don't leak into toasts. */
export function getErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as unknown;
    // Plain-text response body (some endpoints return text/plain on error).
    if (typeof data === 'string' && data.trim() !== '') {
      return data;
    }
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const message = obj.message;
      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
      if (Array.isArray(message)) {
        const joined = message
          .filter((m): m is string => typeof m === 'string' && m.trim() !== '')
          .join('; ');
        if (joined) {
          return joined;
        }
      }
      // Maggie's CustomLoggerExceptionFilter wraps FDHE payloads one level
      // deeper: the human message lives at data.message.message.
      if (message && typeof message === 'object') {
        const inner = (message as Record<string, unknown>).message;
        if (typeof inner === 'string' && inner.trim() !== '') {
          return inner;
        }
      }
      // Other shapes some services use.
      if (typeof obj.error === 'string' && obj.error.trim() !== '') {
        return obj.error;
      }
      if (typeof obj.details === 'string' && obj.details.trim() !== '') {
        return obj.details;
      }
      if (typeof obj.detail === 'string' && obj.detail.trim() !== '') {
        return obj.detail;
      }
    }
    // No usable response body — fall through to axios's own message so we at
    // least show "Request failed with status code 500". A bare "Network Error"
    // (offline / DNS / TLS) is less actionable than the caller's domain-specific
    // fallback ("Failed to upload logo", "Failed to save settings"), so prefer
    // the fallback in that case.
    const isGenericNetworkFailure = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
    if (error.message && !isGenericNetworkFailure) {
      return error.message;
    }
  }
  return fallbackMessage;
}

export function getErrorStatusCode(error: unknown) {
  if (error instanceof AxiosError) {
    return error.response?.status;
  }
  return null;
}
